import { Box, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import PasswordInput from "../../components/ui/PasswordInput";
import Text from "../../components/ui/Text";
import { POST_SIGNIN_REDIRECT_KEY } from "../../constants/auth";
import { useAuth } from "../../contexts/AuthContext";
import apiService from "../../services/api";
import { validateAuthForm, validateField } from "../../utils/authValidation";

const Signin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signin, signInWithGoogle, sendSignInLink, error, setError } =
    useAuth();

  const from = location.state?.from;

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  const [errors, setErrors] = useState({
    email: "",
    password: "",
    general: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Magic link sign-in state
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState("");
  const [magicLinkError, setMagicLinkError] = useState("");
  const [magicLinkSubmitting, setMagicLinkSubmitting] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Clear global error when component mounts
  useEffect(() => {
    setError(null);
  }, [setError]);

  // Show success message if present (e.g., after password reset)
  // useEffect(() => {
  //   if (successMessage) {
  //     showSuccess(successMessage);
  //     // Clear the message from location state
  //     navigate(location.pathname, { replace: true, state: {} });
  //   }
  // }, [successMessage, showSuccess, navigate, location.pathname]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
        general: "", // Clear general error when user starts typing
      });
    }
  };

  const validateForm = () => {
    // Use centralized validation
    const newErrors = validateAuthForm(formData, "signin");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle Google Sign In
  const handleGoogleSignIn = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      const res = await signInWithGoogle();
      if (from) {
        navigate(from, {
          replace: true,
          state: {
            from: window.location.pathname,
            currentUserId: res.user.uid,
          },
        });
        return;
      }
    } catch (error) {
      setErrors((prev) => ({ ...prev, general: error.error }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);

    // Check migrated users
    try {
      const res = await apiService.checkMigratedUser(formData.email);

      if (res.is_migrated) {
        navigate("/reset-password", { state: { email: formData.email } });
        return;
      } else {
        try {
          setError(null);
          const res = await signin(formData.email, formData.password);

          if (from) {
            navigate(from, {
              replace: true,
              state: {
                from: window.location.pathname,
                currentUserId: res.user.uid,
              },
            });
            return;
          }
        } catch (error) {
          // Handle email not verified error
          if (error.code === "auth/email-not-verified") {
            navigate("/confirm-email", {
              replace: true,
              state: {
                email: formData.email,
                password: formData.password,
              },
            });
            return;
          }
        } finally {
          setIsSubmitting(false);
        }
      }
    } catch (error) {
      setErrors((prev) => ({ ...prev, general: error.error }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMagicLinkSubmit = async (e) => {
    e.preventDefault();

    const requiredErr = validateField(magicLinkEmail, "required", "Email");
    const emailErr = requiredErr || validateField(magicLinkEmail, "email");
    if (emailErr) {
      setMagicLinkError(emailErr);
      return;
    }

    try {
      setMagicLinkSubmitting(true);
      setMagicLinkError("");
      setError(null);

      if (from) {
        window.localStorage.setItem(POST_SIGNIN_REDIRECT_KEY, from);
      } else {
        window.localStorage.removeItem(POST_SIGNIN_REDIRECT_KEY);
      }

      const result = await sendSignInLink(magicLinkEmail);
      if (result.success) {
        setMagicLinkSent(true);
      }
    } catch (err) {
      setMagicLinkError(err.friendlyMessage || "Failed to send sign-in link.");
    } finally {
      setMagicLinkSubmitting(false);
    }
  };

  // "Check your email" confirmation view
  if (magicLinkSent) {
    return (
      <>
        <Text variant="pageHeader" sx={{ mb: 2 }}>
          Check your email
        </Text>

        <Text variant="paragraph" sx={{ mb: 4 }}>
          We've sent a sign-in link to <strong>{magicLinkEmail}</strong>
        </Text>

        <Button
          variant="primary"
          onClick={() => {
            setMagicLinkSent(false);
            setShowMagicLink(false);
          }}
          sx={{ width: "100%", mb: 2 }}
        >
          Back to Sign In
        </Button>

        <Text variant="paragraph3" sx={{ color: "#666" }}>
          Didn't receive the email? Check your spam folder or{" "}
          <Button
            variant="text"
            onClick={() => setMagicLinkSent(false)}
            sx={{ p: 0, textDecoration: "underline" }}
          >
            try again
          </Button>
        </Text>
      </>
    );
  }

  // Email-only entry view for magic link sign-in
  if (showMagicLink) {
    return (
      <>
        <Box sx={{ mb: 1 }}>
          <Button
            variant="back"
            onClick={() => {
              setShowMagicLink(false);
              setMagicLinkError("");
            }}
          />
        </Box>
        <Text variant="pageHeader" sx={{ mb: "8px !important" }}>
          Sign in with email link
        </Text>

        <Text variant="paragraph">
          Enter your email address and we'll send you a link to sign in - no
          password needed.
        </Text>

        <form onSubmit={handleMagicLinkSubmit} noValidate>
          <Stack spacing={3} sx={{ mt: "24px !important" }}>
            <Input
              label="Email"
              type="email"
              placeholder="Enter your email"
              value={magicLinkEmail}
              onChange={(e) => {
                setMagicLinkEmail(e.target.value);
                setMagicLinkError("");
              }}
              error={!!magicLinkError}
              helperText={magicLinkError}
            />

            {error && (
              <Text variant="paragraph3" sx={{ color: "#D32F2F" }}>
                {error}
              </Text>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={magicLinkSubmitting}
              sx={{ width: "100%" }}
            >
              {magicLinkSubmitting ? "Sending..." : "Send sign-in link"}
            </Button>
          </Stack>
        </form>
      </>
    );
  }

  return (
    <>
      {/* Welcome Section */}
      <Box sx={{ mb: 3 }}>
        <Text variant="pageHeader" sx={{ mb: "8px !important" }}>
          Welcome to Findaside
        </Text>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Text
            variant="paragraph3"
            sx={{
              fontSize: "16px",
            }}
          >
            Don't have an account?
          </Text>
          <Button
            variant="tertiary"
            onClick={() => navigate("/signup", { state: { from } })}
            sx={{
              color: "#1890FF",
              fontSize: "14px",
              minWidth: "auto",
              p: 1,
            }}
          >
            Sign up
          </Button>
        </Box>
      </Box>

      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={3}>
          {/* Google Button */}
          <Button
            variant="secondary"
            onClick={handleGoogleSignIn}
            disabled={isSubmitting}
            startIcon={
              <img
                src="https://developers.google.com/identity/images/g-logo.png"
                alt="Google"
                style={{ width: 18, height: 18 }}
              />
            }
            sx={{
              justifyContent: "center",
              borderRadius: "4px",
              border: "1px solid black",
            }}
          >
            {isSubmitting ? "Signing in..." : "Continue with Google"}
          </Button>

          {/* Email Link Button */}
          <Button
            variant="secondary"
            onClick={() => {
              setMagicLinkEmail(formData.email);
              setShowMagicLink(true);
            }}
            sx={{
              justifyContent: "center",
              borderRadius: "4px",
              border: "1px solid black",
            }}
          >
            Sign in with email link
          </Button>

          {/* OR Divider */}
          <Box sx={{ textAlign: "center", position: "relative", my: 2 }}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: 0,
                right: 0,
                height: "1px",
                backgroundColor: "#D4D4D4",
                zIndex: 1,
              }}
            />
            <Text
              variant="paragraph3"
              sx={{
                backgroundColor: "white",
                px: 2,
                position: "relative",
                zIndex: 2,
                color: "#666666",
              }}
            >
              OR
            </Text>
          </Box>

          {/* General Error Message */}
          {(errors.general || error) && (
            <Box
              sx={{
                backgroundColor: "#FFF2F0",
                border: "1px solid #FFCCC7",
                borderRadius: "4px",
                padding: "12px",
              }}
            >
              <Text
                variant="paragraph3"
                sx={{
                  color: "#D32F2F",
                }}
              >
                {errors.general || error}
              </Text>
            </Box>
          )}

          {/* Email */}
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleInputChange("email")}
            error={!!errors.email}
            helperText={errors.email}
          />

          {/* Password */}
          <PasswordInput
            label="Password"
            value={formData.password}
            onChange={handleInputChange("password")}
            error={!!errors.password}
            helperText={errors.password}
          />

          {/* Forgot Password */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Button
              variant="tertiary"
              onClick={() => navigate("/forgot-password")}
              sx={{
                color: "#1890FF",
                fontSize: "14px",
                minWidth: "auto",
                p: 0.5,
              }}
            >
              Forgot password?
            </Button>
          </Box>

          {/* Sign In Button */}
          <Button
            variant="primary"
            type="submit"
            disabled={isSubmitting}
            sx={{ mt: 3 }}
          >
            {isSubmitting ? "Signing in..." : "Continue"}
          </Button>
        </Stack>
      </form>
    </>
  );
};

export default Signin;
